/**
 * ProjectListSkeleton - Loading skeleton for project list/grid
 */
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export interface ProjectListSkeletonProps {
  /** Number of skeleton cards to show */
  count?: number
  /** Layout variant */
  variant?: 'grid' | 'list'
  /** Additional CSS classes */
  className?: string
}

/**
 * Skeleton loading state for project list.
 *
 * @example
 * ```tsx
 * // In a loading state
 * if (isLoading) {
 *   return <ProjectListSkeleton count={6} variant="grid" />
 * }
 * ```
 */
export function ProjectListSkeleton({
  count = 4,
  variant = 'grid',
  className
}: ProjectListSkeletonProps) {
  const cards = Array.from({ length: count }, (_, i) => i)

  return (
    <div
      className={cn(
        variant === 'grid'
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
          : 'space-y-4',
        className
      )}
    >
      {cards.map((i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-5 w-32" />
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {/* Description */}
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            {/* Repo info */}
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-40" />
            </div>
            {/* Stats */}
            <div className="flex gap-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
